import {
  DiagnosisCode,
  FacilityCategory,
  ProcedureCode,
  UnitedStates,
  GuidelineType,
  GuidelineBlock,
  Indication,
} from "@coherehealth/core-platform-api";
import Grid from "@material-ui/core/Grid";
import { colorsLight } from "../themes";
import { Caption } from "../components/Typography";
// eslint-disable-next-line cohere-react/no-mui-styled-import
import { makeStyles, styled } from "@material-ui/core/styles";
import { useStableUniqueId } from "../hooks";
import { ReactNode } from "react";
import { formatDateStr, formatDateStrWithMonthName } from "./DateUtils";

export function stateMatch(matchedStateFilters: string): JSX.Element {
  return (
    <>
      {matchedStateFilters && (
        <CommonMatch caption={"State match:"} listItem={[matchedStateFilters] as UnitedStates[]} />
      )}
    </>
  );
}

export function facilityCategoryMatch(matchedFacilityCategoryFilters: string): JSX.Element | null {
  return (
    <>
      {matchedFacilityCategoryFilters && (
        <CommonMatch
          caption={"Facility category match:"}
          listItem={[matchedFacilityCategoryFilters] as FacilityCategory[]}
        />
      )}
    </>
  );
}

export function codeMatch(
  matchedPxCodes: ProcedureCode[] | undefined,
  matchedDxCodes: DiagnosisCode[] | undefined
): JSX.Element {
  return (
    <>
      {!!matchedDxCodes?.length && <CommonMatch caption={"Diagnosis match:"} listItem={matchedDxCodes} isPxDxCode />}
      {!!matchedPxCodes?.length && <CommonMatch caption={"Procedure match:"} listItem={matchedPxCodes} isPxDxCode />}
    </>
  );
}

interface Props {
  caption: string;
  listItem: DiagnosisCode[] | ProcedureCode[] | UnitedStates[] | FacilityCategory[];
  isPxDxCode?: boolean;
}
const useStyles = makeStyles((theme) => ({
  codesContainer: {
    marginBottom: theme.spacing(0.5),
  },
  codes: {
    marginBottom: theme.spacing(1),
  },
}));

export function CommonMatch({ caption, listItem, isPxDxCode }: Props) {
  const classes = useStyles();
  return (
    <Grid item container xs={12} alignItems="flex-start" className={classes.codesContainer}>
      <Grid item xs={3}>
        <MatchCaption>{caption}</MatchCaption>
      </Grid>
      {isPxDxCode ? (
        <Grid item xs={9} style={{ maxWidth: "75%", flexBasis: "0%" }}>
          {listItem.map((code: any, index: number) => (
            <DxPxCode
              key={`${code.id || code.code}_${index}`}
              code={code}
              haveMarginBottom={!Boolean(index + 1 === listItem.length)}
            />
          ))}
        </Grid>
      ) : (
        <Grid item>
          <InfoPill>
            <StyledBody3>{listItem as ReactNode}</StyledBody3>
          </InfoPill>
        </Grid>
      )}
    </Grid>
  );
}

export function DxPxCode({
  code,
  haveMarginBottom,
}: {
  code: ProcedureCode | DiagnosisCode;
  haveMarginBottom: boolean;
}) {
  const uniqueId = useStableUniqueId();
  return (
    <InfoPill key={`pill-${uniqueId}`} style={{ marginBottom: haveMarginBottom ? 8 : 0 }}>
      <TextOverflowBody id={uniqueId}>{`${code.code} - ${code.description}`}</TextOverflowBody>
    </InfoPill>
  );
}

/**
 * methos to decode html entities for indication text, while allowing certain tags to be preserved (e.g. <sup> tags for footnotes)
 * @param str
 * @param tags
 * @returns
 */
export function decodeSelectedEntities(str: string, tags: string[]): string {
  let decoded = str;
  tags.forEach((tag) => {
    const openRegex = new RegExp(`&lt;${tag}&gt;`, "g");
    const closeRegex = new RegExp(`&lt;/${tag}&gt;`, "g");
    decoded = decoded.replace(openRegex, `<${tag}>`).replace(closeRegex, `</${tag}>`);
  });

  // The backend's escapeHtml encodes '&' first, which double-encodes any pre-existing HTML
  // character entities from p.html() output (e.g. &gt; → &amp;gt;). Decode one level back
  // so the browser renders the correct character rather than the literal entity string.
  decoded = decoded.replace(/&amp;(gt|lt|amp|quot|#39|nbsp);/g, "&$1;");
  return decoded;
}

// Return RTE formatting for everything outside of individual list items.
export function getIndicationText(htmlString: string): string {
  const listItems = htmlString.match(/<li/g) || [];
  if (listItems.length !== 1) {
    return htmlString;
  } else {
    const listItemSubstring = htmlString.substring(htmlString.indexOf("<li"), htmlString.indexOf("</li"));
    const replaceableListItemString = listItemSubstring.substring(0, listItemSubstring.indexOf(">"));
    return htmlString.replace(replaceableListItemString, "<div").replace("</li", "</div");
  }
}

export function getIndicationTextForRuleBuild(
  indication: Indication,
  guidelineBlocks: GuidelineBlock[]
): string | undefined {
  if (!indication.indications || indication.indications.length === 0) {
    return getIndicationText(indication.indicationHtml || "- -");
  } else if (indication.guidelineHtmlIndices && guidelineBlocks) {
    const index = Math.min.apply(null, indication.guidelineHtmlIndices);
    if (guidelineBlocks[index]) {
      return guidelineBlocks[index].text;
    } else {
      return "- -";
    }
  } else {
    return "- -";
  }
}

// Finds if a list exists as a wrapper (index 5 because it would be after the initial <div>).
// If it does, then we don't need to add the top/bottom padding to the text. Else, we need it.
export function findList(htmlString: string): boolean {
  const firstUnorderedListIndex = htmlString.indexOf("<ul");
  const firstOrderedListIndex = htmlString.indexOf("<ol");
  return firstUnorderedListIndex === 5 || firstOrderedListIndex === 5;
}

interface URLInfo {
  href: string;
  hostname: string;
}

export function getURLInfo(urlString: string): string | JSX.Element {
  let urlInfo: URLInfo;
  try {
    const isURL = new URL(urlString);
    urlInfo = {
      href: isURL.href,
      hostname: isURL.hostname,
    };
  } catch (error) {
    urlInfo = {
      href: "",
      hostname: urlString,
    };
  }
  if (urlInfo.href === "") {
    return <StyledBody3>{`Policy URL: ${urlInfo.hostname || "--"}`}</StyledBody3>;
  } else {
    return (
      <StyledBody3>
        {"Policy URL: "}
        <PolicyLink href={urlInfo.href} rel="noopener" target="_blank">
          {urlInfo.hostname}
        </PolicyLink>
      </StyledBody3>
    );
  }
}

// eslint-disable-next-line cohere-react/no-mui-styled-import
const MatchCaption = styled(Caption)({
  color: colorsLight.font.secondary,
});

// eslint-disable-next-line cohere-react/no-mui-styled-import
export const InfoPill = styled("div")(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  padding: theme.spacing(1),
  boxShadow: "0px",
  borderRadius: theme.spacing(0.5),
  "&:before": {
    content: "none",
  },
}));

// eslint-disable-next-line cohere-react/no-mui-styled-import
const PolicyLink = styled("a")(() => ({
  textDecoration: "none",
  color: colorsLight.brand.blue,
}));

// eslint-disable-next-line cohere-react/no-mui-styled-import
export const StyledBody3 = styled("div")({
  color: colorsLight.font.main,
  fontFamily: "Figtree",
  fontWeight: 500,
  fontSize: "13px",
});

export const OrderedPolicyTypes: Record<GuidelineType, string> = {
  COHERE_NUDGE: "Cohere Nudge",
  NCD: "NCD",
  LCD: "LCD",
  PAYOR: "Health Plan Guidelines",
  COHERE: "Cohere Guidelines",
  ALTERNATIVE: "Alternative Guidelines",
  INTERQUAL: "InterQual",
  HMSA_POLICY: "HMSA Policy",
  NASS: "NASS",
  MCG: "MCG",
};

// eslint-disable-next-line cohere-react/no-mui-styled-import
export const TextOverflowBody = styled(StyledBody3)(() => ({
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
}));

export function isPolicyPlatformCoherePolicy(fromPolicyManagement?: boolean, policyType?: string | null): boolean {
  return !!(fromPolicyManagement && policyType === "COHERE");
}

const COLLAPSIBLE_POLICY_TYPES = new Set(["INTERQUAL"]);

export function isPMSharedGuidelinePreview(
  useInteractiveRichTextReview: boolean,
  fromPolicyManagement?: boolean,
  policyType?: string
): boolean {
  return !!(
    useInteractiveRichTextReview &&
    fromPolicyManagement &&
    policyType &&
    COLLAPSIBLE_POLICY_TYPES.has(policyType.toUpperCase())
  );
}

export function formatCoherePolicyTitle(
  policyTitle: string | undefined,
  publicVersionNumber: string | undefined | null,
  effectiveDate: string | undefined,
  useMonthName = false
): string | undefined {
  if (!policyTitle) {
    return policyTitle;
  }
  const parts: string[] = [policyTitle];
  if (publicVersionNumber) {
    parts.push(`Version ${publicVersionNumber}`);
  }
  if (effectiveDate) {
    const dateStr = useMonthName ? formatDateStrWithMonthName(effectiveDate) : formatDateStr(effectiveDate);
    if (dateStr) {
      parts.push(dateStr);
    }
  }
  return parts.join(", ");
}
